import React, { useCallback, useRef, useState } from 'react';

import styles from './ElementSection.module.css';
import {
  addElementContentBlock,
  clearSelectedItem,
  moveElementitem,
  selectSelectedItem,
} from 'features/document/documentSlice';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { Element, ElementType } from 'features/document/constants';
import { DragPreviewImage, XYCoord, useDrag, useDrop } from 'react-dnd';
import { boxImage } from '../RowSection/BoxImage';
import SelectedItem from 'features/document/components/SelectedItem/SelectedItem';
import ComponentFactory from './components/ComponentFactory/ComponentFactory';
import { ElementSide, PagePartType } from 'features/document/constants';
import { useDropUpdateContext } from 'pages/DocumentBuilder/components/DropContext';

interface Props {
  currentElement: Element;
  pageId: string;
  index: number;
  rowId: string;
  columnId: string;
}

interface DragElement {
  pageId: string;
  index: number;
  rowId: string;
  columnId: string;
  currentElement: Element;
}

type DraggableTypes =
  | { type: PagePartType.Block; elementType: string }
  | ({ type: PagePartType.Element } & DragElement);

const ElementSection: React.FC<Props> = ({ currentElement, pageId, index, rowId, columnId }) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ref = useRef<any>(null);
  const dispatch = useAppDispatch();
  const selector = useAppSelector;
  const updateStatus = useDropUpdateContext();

  const selectedItem = selector(selectSelectedItem);
  const selectedItemType = selectedItem?.data?.type;
  const isTable =
    selectedItemType === ElementType.Table ||
    selectedItemType === ElementType.PricingTable ||
    selectedItemType === ElementType.QuoteAcceptanceInfoTable;

  const [borderHighlight, setBorderHighlight] = useState({
    top: false,
    bottom: false,
    left: false,
    right: false,
  });

  const updateBorderHighlight = useCallback((clientOffset: XYCoord) => {
    const hoverBoundingRect = ref.current?.getBoundingClientRect();
    const middleRect = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
    const leftRightArea = (hoverBoundingRect.right - hoverBoundingRect.left) / 8;

    const hoverClientY = clientOffset.y - hoverBoundingRect.top;
    const hoverClientLeft = clientOffset.x - hoverBoundingRect.left;
    const hoverClientRight = hoverBoundingRect.right - clientOffset.x;

    const isRightSide = hoverClientRight < leftRightArea;
    const isLeftSide = hoverClientLeft < leftRightArea;

    const isSide = isRightSide || isLeftSide;

    setBorderHighlight({
      top: !isSide && hoverClientY < middleRect,
      bottom: !isSide && hoverClientY > middleRect,
      left: isLeftSide,
      right: isRightSide,
    });
  }, []);

  const [{ isOver }, drop] = useDrop<DraggableTypes, void, { isOver: boolean }>(
    () => ({
      accept: [PagePartType.Element, PagePartType.Block],
      collect: monitor => ({
        isOver: monitor.isOver(),
      }),
      hover: (item, monitor) => {
        if (!ref.current) {
          return;
        }

        updateStatus({ page: false, row: false, element: true });

        if (item.type === PagePartType.Element && currentElement.id === item.currentElement.id) {
          return;
        }
        const clientOffset = monitor.getClientOffset();
        if (clientOffset) {
          updateBorderHighlight(clientOffset as XYCoord);
        }
      },
      drop: (item, _monitor) => {
        if (borderHighlight.bottom) {
          if (item.type === PagePartType.Element)
            dispatch(
              moveElementitem({
                currentElement,
                pageId,
                rowId,
                columnId,
                index,
                side: ElementSide.Bottom,
              })
            );
          else if (item.type === PagePartType.Block) {
            dispatch(
              addElementContentBlock({
                pageId,
                rowId,
                columnId,
                index,
                side: ElementSide.Bottom,
                type: item.elementType,
              })
            );
          }
        }
        if (borderHighlight.top) {
          if (item.type === PagePartType.Element)
            dispatch(
              moveElementitem({
                currentElement,
                pageId,
                rowId,
                columnId,
                index,
                side: ElementSide.Top,
              })
            );
          else if (item.type === PagePartType.Block) {
            dispatch(
              addElementContentBlock({
                pageId,
                rowId,
                columnId,
                index,
                side: ElementSide.Top,
                type: item.elementType,
              })
            );
          }
        }
        if (borderHighlight.left && !isTable) {
          if (item.type === PagePartType.Element)
            dispatch(
              moveElementitem({
                currentElement,
                pageId,
                rowId,
                columnId,
                index,
                side: ElementSide.Left,
              })
            );
          else if (item.type === PagePartType.Block) {
            dispatch(
              addElementContentBlock({
                pageId,
                rowId,
                columnId,
                index,
                side: ElementSide.Left,
                type: item.elementType,
              })
            );
          }
        }
        if (borderHighlight.right && !isTable) {
          if (item.type === PagePartType.Element)
            dispatch(
              moveElementitem({
                currentElement,
                pageId,
                rowId,
                columnId,
                index,
                side: ElementSide.Right,
              })
            );
          else if (item.type === PagePartType.Block) {
            dispatch(
              addElementContentBlock({
                pageId,
                rowId,
                columnId,
                index,
                side: ElementSide.Right,
                type: item.elementType,
              })
            );
          }
        }
        dispatch(clearSelectedItem({}));
      },
    }),
    [borderHighlight]
  );

  const [{ isDragging }, drag, preview] = useDrag(() => ({
    type: PagePartType.Element,
    item: { currentElement, pageId, index, type: PagePartType.Element },
    collect: monitor => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  drop(ref);

  const isHovering = isOver && !isDragging;

  return (
    <div
      style={{
        opacity: isDragging ? 0.5 : 1,
      }}
      ref={ref}
      className={styles.mainContainer}
    >
      {isHovering && (
        <>
          <div className={styles.containerLineTop}>
            <div className={styles.line} style={{ opacity: borderHighlight.top ? 1 : 0.5 }} />
          </div>
          <div className={styles.containerLineBot}>
            <div className={styles.line} style={{ opacity: borderHighlight.bottom ? 1 : 0.5 }} />
          </div>
          <div className={styles.containerLineLeft} style={{ display: isTable ? 'none' : 'block' }}>
            <div className={styles.lineLeft} style={{ opacity: borderHighlight.left ? 1 : 0.5 }} />
          </div>
          <div className={styles.containerLineRight} style={{ display: isTable ? 'none' : 'block' }}>
            <div className={styles.lineLeft} style={{ opacity: borderHighlight.right ? 1 : 0.5 }} />
          </div>
        </>
      )}
      <DragPreviewImage connect={preview} src={boxImage} />
      <div
        style={{
          pointerEvents: isHovering ? 'none' : 'auto',
        }}
      >
        <SelectedItem
          isDragging={isDragging}
          data={{
            data: currentElement,
            pageId,
            index,
            rowId,
            columnId,
            type: PagePartType.Element,
          }}
          ref={drag}
        >
          <ComponentFactory currentElement={currentElement} />
        </SelectedItem>
      </div>
    </div>
  );
};

export default ElementSection;
